import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Briefcase, MessageSquare, DollarSign,
  Clock, ChevronRight, Calendar, MapPin, Video, FileText, User
} from 'lucide-react';
import clientPortalService from '../../services/clientPortalService';
import StatusHistoryTimeline from '../../components/StatusHistory/StatusHistoryTimeline';

const CATEGORY_COLORS = {
  court: '#EF4444', hearing: '#EF4444', court_appearance: '#EF4444',
  deadline: '#F97316', trial: '#DC2626',
  meeting: '#0EA5E9', consultation: '#8B5CF6',
  deposition: '#06B6D4', mediation: '#6366F1',
};

const KIND_META = {
  appointment: { label: 'Appointment', dot: '#06B6D4' },
  court_date: { label: 'Court date', dot: '#EF4444' },
  hearing: { label: 'Hearing', dot: '#EF4444' },
};

const TimelineEventRow = ({ ev }) => {
  const meta = KIND_META[ev.kind] || { label: (ev.kind || 'event').replace(/_/g, ' '), dot: '#6B7280' };
  return (
    <div className={`rounded-lg p-3 ${ev.is_past ? 'bg-virgo-navy-800/30' : 'bg-virgo-navy-800/50'}`}>
      <div className="flex items-start gap-3 min-w-0">
        <span className="w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0" style={{ background: meta.dot, opacity: ev.is_past ? 0.5 : 1 }} />
        <div className="min-w-0 flex-1">
          <p className="text-[10px] text-virgo-teal-500 uppercase tracking-wider font-medium">{meta.label}</p>
          <p className={`font-medium break-words ${ev.is_past ? 'text-virgo-teal-300' : 'text-white'}`}>{ev.title}</p>
          {ev.location && (
            <p className="text-xs text-virgo-teal-400 italic flex items-start gap-1 mt-0.5">
              <MapPin className="w-3 h-3 flex-shrink-0 mt-0.5" /><span className="break-words">{ev.location}</span>
            </p>
          )}
          <p className={`text-xs mt-1 ${ev.is_past ? 'text-virgo-teal-600' : 'text-virgo-teal-300'}`}>
            {ev.local_label || 'Date to be scheduled'}
          </p>
        </div>
      </div>
    </div>
  );
};

// Whitelist-driven, firm-local case schedule (server returns only client-visible
// kinds: appointments, court dates, hearings). Past vs upcoming rendered distinctly.
const CaseCalendarWidget = ({ caseId }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    clientPortalService.getCaseTimeline(caseId)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData({ events: [], counts: {} }); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [caseId]);

  const card = 'bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-5 sm:p-6 mb-6';
  const header = (
    <h2 className="text-lg font-bold text-white mb-4 flex items-center">
      <Calendar className="text-cyan-400 mr-2" size={20} /> Schedule
    </h2>
  );

  if (loading) {
    return (
      <div className={card}>
        {header}
        <div className="space-y-3 animate-pulse">
          <div className="h-16 bg-virgo-navy-800/50 rounded-lg" />
          <div className="h-16 bg-virgo-navy-800/50 rounded-lg" />
        </div>
      </div>
    );
  }

  const events = (data && data.events) || [];
  const upcoming = events.filter((e) => !e.is_past);
  const past = events.filter((e) => e.is_past);

  return (
    <div className={card}>
      {header}
      {events.length === 0 ? (
        <div className="text-center py-8">
          <Calendar className="text-virgo-teal-600/60 mx-auto mb-3" size={30} />
          <p className="text-virgo-teal-400 text-sm">No scheduled events</p>
          <p className="text-virgo-teal-600 text-xs mt-1">Appointments and court dates will appear here.</p>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            {upcoming.length === 0 ? (
              <p className="text-virgo-teal-500 text-sm py-2">No upcoming events.</p>
            ) : (
              upcoming.map((ev) => <TimelineEventRow key={ev.id} ev={ev} />)
            )}
          </div>
          {past.length > 0 && (
            <div className="mt-4 pt-3 border-t border-virgo-navy-600/50">
              <button
                onClick={() => setShowPast((v) => !v)}
                className="text-cyan-400 hover:text-cyan-300 text-sm flex items-center gap-1"
              >
                <Clock className="w-3.5 h-3.5" />
                {showPast ? 'Hide' : 'Show'} {past.length} past event{past.length > 1 ? 's' : ''}
              </button>
              {showPast && (
                <div className="space-y-2 mt-2 opacity-75">
                  {past.map((ev) => <TimelineEventRow key={ev.id} ev={ev} />)}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PortalCases = () => {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [cases, setCases] = useState([]);
  const [caseDetail, setCaseDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (caseId) {
      loadCaseDetail();
    } else {
      loadCases();
    }
  }, [caseId]);

  const loadCases = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService.getCases();
      setCases(data);
    } catch (err) {
      setError('Failed to load cases');
    } finally {
      setLoading(false);
    }
  };

  const loadCaseDetail = async () => {
    try {
      setLoading(true);
      const data = await clientPortalService.getCaseDetail(caseId);
      setCaseDetail(data);
    } catch (err) {
      setError('Failed to load case details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending': return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
      case 'on_hold': return 'bg-purple-500/20 text-purple-300 border-purple-500/30';
      case 'closed': return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
      default: return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
  };

  const getStageLabel = (stage) => {
    const stages = {
      intake: 'Intake',
      investigation: 'Investigation',
      pre_litigation: 'Pre-Litigation',
      litigation_filed: 'Litigation Filed',
      discovery: 'Discovery',
      mediation: 'Mediation',
      trial_prep: 'Trial Preparation',
      trial: 'Trial',
      post_trial: 'Post-Trial',
      appeal: 'Appeal',
      settlement: 'Settlement',
      on_hold: 'On Hold',
      closed: 'Closed',
    };
    return stages[stage] || stage;
  };

  const getStageBadgeColor = (stage) => {
    const colors = {
      intake: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      investigation: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      pre_litigation: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
      litigation_filed: 'bg-orange-500/20 text-orange-300 border-orange-500/30',
      discovery: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',
      mediation: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      trial_prep: 'bg-red-500/20 text-red-300 border-red-500/30',
      trial: 'bg-red-600/20 text-red-400 border-red-600/30',
      post_trial: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30',
      on_hold: 'bg-purple-500/20 text-purple-300 border-purple-500/30',
      appeal: 'bg-violet-500/20 text-violet-300 border-violet-500/30',
      settlement: 'bg-green-500/20 text-green-300 border-green-500/30',
      closed: 'bg-slate-500/20 text-slate-300 border-slate-500/30',
    };
    return colors[stage] || 'bg-slate-500/20 text-slate-300 border-slate-500/30';
  };

  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));

    const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const dateFormatted = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

    if (diffDays === 0) return `Today at ${timeStr}`;
    if (diffDays === 1) return `Tomorrow at ${timeStr}`;
    if (diffDays < 7) return `${dateFormatted} at ${timeStr}`;
    return `${dateFormatted} at ${timeStr}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // Case Detail View
  if (caseId && caseDetail) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
        {/* Header */}
        <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
            <button
              onClick={() => navigate('/portal/cases')}
              className="text-virgo-teal-400 hover:text-white transition-colors"
            >
              <ArrowLeft size={24} />
            </button>
            <div className="flex items-center space-x-3">
              <Briefcase className="text-cyan-400" size={24} />
              <h1 className="text-xl font-bold text-white">{caseDetail.case_name}</h1>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="max-w-7xl mx-auto px-4 py-8">
          {error && (
            <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 text-red-300">
              {error}
            </div>
          )}

          {/* Case Overview */}
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(caseDetail.status)}`}>
                    {String(caseDetail.status || '').replace('_', ' ')}
                  </span>
                  {caseDetail.matter_stage && getStageLabel(caseDetail.matter_stage).toLowerCase() !== (caseDetail.status||'').toLowerCase() && (
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${getStageBadgeColor(caseDetail.matter_stage)}`}>
                      {getStageLabel(caseDetail.matter_stage)}
                    </span>
                  )}
                </div>
                <p className="text-virgo-teal-400">Case #{caseDetail.case_number}</p>
                {caseDetail.practice_area && (
                  <p className="text-virgo-teal-500 text-sm">{caseDetail.practice_area}</p>
                )}
              </div>
              {caseDetail.assigned_attorney && (
                <div className="flex items-center space-x-2 text-virgo-teal-300">
                  <User size={16} />
                  <span>{caseDetail.assigned_attorney}</span>
                </div>
              )}
            </div>

            {caseDetail.description && (
              <p className="text-virgo-teal-200 mb-4">{caseDetail.description}</p>
            )}

            {/* Client Update */}
            {caseDetail.client_update && (
              <div className="bg-slate-900/20 border border-cyan-500/30 rounded-lg p-4 mb-4">
                <p className="text-sm text-cyan-200">
                  <span className="font-medium text-cyan-300">Latest Update: </span>
                  {caseDetail.client_update}
                </p>
              </div>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 text-center">
                <Clock className="text-virgo-teal-400 mx-auto mb-1" size={20} />
                <p className="text-xs text-virgo-teal-500">Opened</p>
                <p className="text-white font-medium">
                  {caseDetail.open_date ? new Date(caseDetail.open_date).toLocaleDateString() : 'N/A'}
                </p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 text-center">
                <MessageSquare className="text-cyan-400 mx-auto mb-1" size={20} />
                <p className="text-xs text-virgo-teal-500">Unread</p>
                <p className="text-white font-medium">{caseDetail.unread_messages || 0}</p>
              </div>
              <div className="bg-virgo-navy-800/50 rounded-lg p-3 text-center">
                <DollarSign className="text-amber-400 mx-auto mb-1" size={20} />
                <p className="text-xs text-virgo-teal-500">Outstanding</p>
                <p className="text-white font-medium">${(caseDetail.total_outstanding || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Upcoming Events - Enhanced with visibility enforcement */}
          <CaseCalendarWidget
            caseId={caseId}
            caseName={caseDetail.case_name}
            existingEvents={caseDetail.upcoming_events}
          />

          {/* Recent Updates */}
          {caseDetail.recent_updates && caseDetail.recent_updates.length > 0 && (
            <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 mb-6">
              <h2 className="text-lg font-bold text-white mb-4">Recent Updates</h2>
              <div className="space-y-3">
                {caseDetail.recent_updates.map((update, idx) => (
                  <div key={idx} className="bg-virgo-navy-800/50 rounded-lg p-3">
                    <p className="text-white">{update.content || update.message}</p>
                    <p className="text-xs text-virgo-teal-500 mt-1">
                      {new Date(update.created_at || update.date).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Status History */}
          <div className="mb-6">
            <StatusHistoryTimeline
              entityType="case"
              entityId={caseId}
              portal={true}
              limit={10}
            />
          </div>

          {/* Quick Actions */}
          <div className="grid md:grid-cols-3 gap-4">
            <Link
              to="/portal/messages"
              className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4 hover:border-cyan-500/50 transition-all group flex items-center space-x-3"
            >
              <MessageSquare className="text-cyan-400 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-white">Send Message</span>
            </Link>
            <Link
              to="/portal/documents"
              className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4 hover:border-cyan-500/50 transition-all group flex items-center space-x-3"
            >
              <FileText className="text-blue-400 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-white">View Documents</span>
            </Link>
            <Link
              to="/portal/invoices"
              className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-4 hover:border-cyan-500/50 transition-all group flex items-center space-x-3"
            >
              <DollarSign className="text-amber-400 group-hover:scale-110 transition-transform" size={24} />
              <span className="text-white">View Invoices</span>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Cases List View
  return (
    <div className="min-h-screen bg-gradient-to-br from-virgo-navy-900 via-virgo-navy-800 to-virgo-navy-900">
      {/* Header */}
      <header className="bg-virgo-navy-800/80 border-b border-virgo-teal-700/30 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <button
            onClick={() => navigate('/portal/dashboard')}
            className="text-virgo-teal-400 hover:text-white transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex items-center space-x-3">
            <Briefcase className="text-cyan-400" size={24} />
            <h1 className="text-xl font-bold text-white">My Cases</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 mb-6 text-red-300">
            {error}
          </div>
        )}

        {cases.length === 0 ? (
          <div className="bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-12 text-center">
            <Briefcase className="text-virgo-teal-500 mx-auto mb-4" size={48} />
            <h2 className="text-xl font-semibold text-white mb-2">No Cases Found</h2>
            <p className="text-virgo-teal-400">You don't have any cases associated with your account yet.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {cases.map((caseItem) => (
              <Link
                key={caseItem.case_id}
                to={`/portal/cases/${caseItem.case_id}`}
                className="block bg-gradient-to-br from-virgo-navy-700 to-virgo-navy-800 rounded-xl border border-virgo-teal-700/30 p-6 hover:border-cyan-500/50 transition-all group"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h2 className="text-lg font-semibold text-white group-hover:text-cyan-400 transition-colors">
                        {caseItem.case_name}
                      </h2>
                      <span className={`px-2 py-0.5 text-xs rounded-full border ${getStatusColor(caseItem.status)}`}>
                        {String(caseItem.status || '').replace('_', ' ')}
                      </span>
                      {caseItem.matter_stage && getStageLabel(caseItem.matter_stage).toLowerCase() !== (caseItem.status||'').toLowerCase() && (
                        <span className={`px-2 py-0.5 text-xs rounded-full border ${getStageBadgeColor(caseItem.matter_stage)}`}>
                          {getStageLabel(caseItem.matter_stage)}
                        </span>
                      )}
                    </div>

                    <p className="text-sm text-virgo-teal-400 mb-3">
                      Case #{caseItem.case_number}
                      {caseItem.practice_area && ` - ${caseItem.practice_area}`}
                    </p>

                    {/* Next Event */}
                    {caseItem.next_event && (
                      <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 border border-cyan-500/30 rounded-lg p-3 mb-3">
                        <div className="flex items-start gap-3">
                          <Calendar className="w-5 h-5 text-cyan-400 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-cyan-300">Next Event:</span>
                              <span className="text-white">{caseItem.next_event.title}</span>
                            </div>
                            <p className="text-sm text-cyan-200 mt-1">
                              {formatEventDate(caseItem.next_event.start_time)}
                            </p>
                            {caseItem.next_event.location && (
                              <p className="text-xs text-cyan-300 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {caseItem.next_event.location}
                              </p>
                            )}
                            {caseItem.next_event.visage_meeting_link && (
                              <a
                                href={caseItem.next_event.visage_meeting_link}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 mt-1"
                              >
                                <Video className="w-3 h-3" />
                                Join Video Meeting
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Client Update Message */}
                    {caseItem.client_update && (
                      <div className="bg-slate-900/20 border border-cyan-500/30 rounded-lg p-3 mb-3">
                        <p className="text-sm text-cyan-200">
                          <span className="font-medium text-cyan-300">Latest Update: </span>
                          {caseItem.client_update}
                        </p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-4 text-sm">
                      {caseItem.assigned_attorney && (
                        <span className="text-virgo-teal-300">
                          Attorney: {caseItem.assigned_attorney}
                        </span>
                      )}
                      {caseItem.open_date && (
                        <span className="text-virgo-teal-400 flex items-center">
                          <Clock size={14} className="mr-1" />
                          Opened: {new Date(caseItem.open_date).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4 flex-shrink-0">
                    {/* Stats */}
                    <div className="flex items-center space-x-4 text-sm">
                      {caseItem.unread_messages > 0 && (
                        <div className="flex items-center text-cyan-400">
                          <MessageSquare size={16} className="mr-1" />
                          <span>{caseItem.unread_messages}</span>
                        </div>
                      )}
                      {caseItem.total_outstanding > 0 && (
                        <div className="flex items-center text-amber-400">
                          <DollarSign size={16} className="mr-1" />
                          <span>${caseItem.total_outstanding.toLocaleString()}</span>
                        </div>
                      )}
                    </div>
                    <ChevronRight className="text-virgo-teal-500 group-hover:text-cyan-400 transition-colors" size={24} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PortalCases;
